import {
  ArrowForward as ArrowIcon,
  Computer as BrowserIcon,
  HealthAndSafety as CheckIcon,
  ChevronRight as CollapseIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandIcon,
  BugReport as IssueIcon,
  OpenInNew as OpenIcon,
  Subscriptions as RunIcon,
  SaveAlt as SaveIcon,
  Folder as SuiteIcon,
  Science as TestIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router";
import {
  useArgusAgentSessionBrowserSessions,
  useArgusAgentSessionTestRuns,
} from "../../hooks/argus";
import { appendArgusParams } from "../../lib/argus/argus-params";
import { extractResources, type RelatedResource } from "../../lib/argus/resource-extractor";
import { BRAND_PRIMARY, RADIUS_MD } from "../../theme/theme";
import type { TestRun } from "../../types";
import type { ArgusGatewayEvent } from "../../types/argus-events";
import type { BrowserSession } from "../../types/browser-session";
import { BrowserSessionStatusChip } from "../browser-session-status-chip";
import { LiveView } from "../live-view";
import { RunStatusChip } from "../run-status-chip";

/** Must match the constant in `pages/argus.tsx` so the three panel
 *  headers all sit on the same horizontal baseline. */
const HEADER_HEIGHT = 56;

export interface ArgusRelatedResourcesPanelProps {
  events: ArgusGatewayEvent[];
  /** Cope-side ArgusSession id (used only for URL param passthrough on
   *  navigate-derived resources — not for artifact fetching). */
  sessionId: string | null;
  /** Pi agent session id — the attribution key the gateway stamps onto
   *  every session-scoped artifact (`created_by = agent:<id>`). Drives
   *  both artifact fetches in this panel. */
  agentSessionId: string | null;
  /** Argus instance id — still required as a URL param for navigate
   *  resources so deep links retain the instance context. */
  instanceId: string | null;
  /** Optional callback — when provided, a collapse chevron is rendered in the header. */
  onCollapse?: () => void;
  /** Optional callback — fires whenever the panel's total artifact count
   *  changes. Lets the parent badge / auto-expand without re-deriving the
   *  count itself (panel stays the single source of truth). */
  onTotalCountChange?: (count: number) => void;
}

const TYPE_ICON: Record<RelatedResource["type"], typeof OpenIcon> = {
  test_detail: TestIcon,
  test_run_detail: RunIcon,
  test_suite_detail: SuiteIcon,
  check_detail: CheckIcon,
  browser_sessions_list: BrowserIcon,
  browser_session_detail: BrowserIcon,
  issue_detail: IssueIcon,
  navigation: ArrowIcon,
};

const CLOSED_STATUSES: BrowserSession["status"][] = ["closing", "closed", "error", "failed"];

const isClosed = (session: BrowserSession) => CLOSED_STATUSES.includes(session.status);

const TEST_RUN_STATUS_COLOR: Record<string, ChipColor> = {
  passed: "success",
  succeeded: "success",
  pending: "warning",
  running: "warning",
  queued: "warning",
  failed: "error",
  errored: "error",
};

type ChipColor = "success" | "default" | "warning" | "error";

const STATUS_COLOR: Partial<Record<BrowserSession["status"], ChipColor>> = {
  starting: "warning",
  active: "success",
  closing: "default",
  closed: "default",
  error: "error",
  failed: "error",
};

/** MUI palette path for the status rail — subtle color accent on the left
 *  edge of each card that signals status without needing to read the chip. */
const STATUS_RAIL: Record<ChipColor, string> = {
  success: "success.main",
  warning: "warning.main",
  error: "error.main",
  default: "divider",
};

/**
 * Artifacts panel for the right column of `/argus`.
 *
 * Shows, for the currently-selected Argus agent session:
 *  1. Browser sessions created during the session — each a collapsible
 *     card with a liveview (active) or inline recording player (closed).
 *     All cards link to the full browser-session detail page.
 *  2. Navigate-derived resources (tests, runs, etc.) from the event buffer.
 */
export const ArgusRelatedResourcesPanel = ({
  events,
  sessionId,
  agentSessionId,
  instanceId,
  onCollapse,
  onTotalCountChange,
}: ArgusRelatedResourcesPanelProps) => {
  const navigate = useNavigate();
  // Both fetches are session-scoped at the server: they query by
  // `created_by = agent:<pi_session_id>`, which the gateway stamps onto
  // every session-scoped artifact. No client-side time-window filter is
  // needed — the returned arrays already belong to exactly this agent
  // session.
  const sessionBrowserSessions = useArgusAgentSessionBrowserSessions(agentSessionId);
  const sessionTestRuns = useArgusAgentSessionTestRuns(agentSessionId);

  // Events are buffered in argus.tsx's SSE stream and can contain
  // navigate() calls from a previously-selected session. Guard the
  // resource extractor so those stale entries don't leak into a newly-
  // selected (or "New") session's panel.
  const hasSession = !!sessionId;
  const resources = useMemo(
    () => (hasSession ? extractResources(events) : []),
    [hasSession, events],
  );

  const totalCount = sessionBrowserSessions.length + sessionTestRuns.length + resources.length;

  useEffect(() => {
    onTotalCountChange?.(totalCount);
  }, [totalCount, onTotalCountChange]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid",
        borderColor: "divider",
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: HEADER_HEIGHT,
          flexShrink: 0,
          paddingX: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Artifacts
          </Typography>
          {totalCount > 0 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {totalCount}
            </Typography>
          )}
        </Stack>
        {onCollapse && (
          <Tooltip title="Collapse" arrow>
            <IconButton size="small" onClick={onCollapse} aria-label="Collapse artifacts panel">
              <CollapseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* `containerType: inline-size` establishes a query container so the
          cards below can shrink their internals via `@container` queries
          regardless of the outer viewport width. This gives us real
          responsiveness to the artifacts panel's actual width instead of
          the window width. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          padding: 1.25,
          containerType: "inline-size",
        }}
      >
        {totalCount === 0 ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Agent actions will appear here.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {sessionBrowserSessions.map((session, idx) => (
              <BrowserSessionCard key={session.id} session={session} defaultExpanded={idx === 0} />
            ))}
            {sessionTestRuns.map((run) => (
              <TestRunCard key={run.id} run={run} defaultExpanded={false} />
            ))}
            {resources.map((resource) => {
              const Icon = TYPE_ICON[resource.type] ?? OpenIcon;
              return (
                <Box
                  key={resource.id}
                  onClick={() =>
                    navigate(
                      appendArgusParams(resource.path, {
                        sessionId,
                        instanceId,
                      }),
                    )
                  }
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: `${RADIUS_MD}px`,
                    padding: 1.5,
                    cursor: "pointer",
                    transition: "border-color 0.15s",
                    "&:hover": { borderColor: BRAND_PRIMARY },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Icon fontSize="small" sx={{ color: BRAND_PRIMARY, marginTop: "2px" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {resource.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontFamily: "monospace",
                        }}
                        noWrap
                      >
                        {resource.path}
                      </Typography>
                      {resource.reason && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "text.secondary",
                            marginTop: 0.25,
                          }}
                        >
                          {resource.reason}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// BrowserSessionCard
// ============================================================================

interface BrowserSessionCardProps {
  session: BrowserSession;
  defaultExpanded: boolean;
}

const BrowserSessionCard = ({ session, defaultExpanded }: BrowserSessionCardProps) => {
  const closed = isClosed(session);
  const statusColor = STATUS_COLOR[session.status] ?? "default";
  const railColor = STATUS_RAIL[statusColor];

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderLeft: "3px solid",
        borderLeftColor: railColor,
        borderRadius: `${RADIUS_MD}px`,
        overflow: "hidden",
        "&:before": { display: "none" },
        boxShadow: "none",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon fontSize="small" />}
        sx={{
          paddingX: 1,
          paddingY: 0,
          minHeight: 36,
          "& .MuiAccordionSummary-content": { margin: "8px 0" },
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ width: "100%", minWidth: 0 }}
        >
          <BrowserIcon fontSize="small" sx={{ color: BRAND_PRIMARY, flexShrink: 0 }} />
          <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0}>
            <Stack direction="row" spacing={0.75} alignItems="baseline">
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, flexShrink: 0, fontSize: "12px" }}
              >
                Browser
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontFamily: "monospace",
                  flexShrink: 0,
                  fontSize: "10px",
                }}
              >
                {session.id.slice(0, 8)}
              </Typography>
            </Stack>
            {session.current_url && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "10px",
                  lineHeight: 1.3,
                  // Hide URL line when the card's container gets narrow.
                  "@container (max-width: 260px)": { display: "none" },
                }}
                noWrap
              >
                {session.current_url}
              </Typography>
            )}
          </Stack>
          {/* At narrow container widths drop the chip label and keep only
              the leading status icon — saves ~60–80px per row without
              losing any information the color rail already conveys. */}
          <Box
            sx={{
              flexShrink: 0,
              "@container (max-width: 280px)": {
                "& .MuiChip-label": { display: "none" },
                "& .MuiChip-root": { paddingLeft: "4px", paddingRight: "4px" },
                "& .MuiChip-icon": { marginLeft: 0, marginRight: 0 },
              },
            }}
          >
            <BrowserSessionStatusChip
              status={session.status}
              size="small"
              variant={closed ? "outlined" : "filled"}
            />
          </Box>
          <Tooltip title="Open session details" arrow>
            <IconButton
              component={Link}
              to={`/browser-sessions/${session.id}`}
              size="small"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open browser session details"
              sx={{
                flexShrink: 0,
                padding: 0.25,
                // Drop the external-link icon when space is tight — the
                // whole card is already clickable via the accordion.
                "@container (max-width: 240px)": { display: "none" },
              }}
            >
              <OpenIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 1, paddingTop: 0 }}>
        {/* Primary media region — inline preview whenever possible. The
            aspect-ratio wrapper keeps the 16:9 viewport proportional and
            the maxHeight cap prevents it from dominating the card when
            the panel is wide. */}
        {!closed && session.liveview_url ? (
          <Box sx={{ width: "100%", aspectRatio: "16 / 9", maxHeight: 200 }}>
            <LiveView
              liveViewUrl={session.liveview_url}
              pageUrl={session.current_url ?? undefined}
              height="100%"
              noLiveViewMessage="Browser session not yet ready."
            />
          </Box>
        ) : closed && session.recording_url ? (
          <RecordingFrame
            url={session.recording_url}
            caption={`Recorded ${
              session.last_action_at
                ? new Date(session.last_action_at).toLocaleTimeString()
                : "session"
            }`}
          />
        ) : !closed ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Starting browser — no live view yet.
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Session {session.status}
            {session.last_action_at
              ? ` at ${new Date(session.last_action_at).toLocaleTimeString()}`
              : ""}
            . No recording available.
          </Typography>
        )}

        {session.error_message && (
          <Typography variant="caption" color="error" sx={{ display: "block", marginTop: 1 }}>
            {session.error_message}
          </Typography>
        )}

        {/* Secondary artifact links — recording shown inline above if present,
            keep direct download links alongside HAR / storage state. */}
        {(session.har_url || session.storage_state_url) && (
          <Stack direction="row" spacing={2} sx={{ marginTop: 1.25, alignItems: "center" }}>
            {session.har_url && (
              <MuiLink
                href={session.har_url}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.secondary",
                  textDecorationColor: "currentColor",
                }}
              >
                <DescriptionIcon sx={{ fontSize: 14 }} />
                HAR
              </MuiLink>
            )}
            {session.storage_state_url && (
              <MuiLink
                href={session.storage_state_url}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.secondary",
                  textDecorationColor: "currentColor",
                }}
              >
                <SaveIcon sx={{ fontSize: 14 }} />
                Storage state
              </MuiLink>
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// ============================================================================
// RecordingFrame — lightweight video player matching LiveView visual language
// ============================================================================

interface RecordingFrameProps {
  url: string;
  caption?: string;
}

const RecordingFrame = ({ url, caption }: RecordingFrameProps) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "grey.400",
      borderRadius: `${RADIUS_MD}px`,
      overflow: "hidden",
      backgroundColor: "common.black",
    }}
  >
    {caption && (
      <Box
        sx={{
          backgroundColor: "grey.100",
          borderBottom: "1px solid",
          borderColor: "grey.400",
          paddingX: 1,
          paddingY: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minHeight: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", flex: 1, fontSize: "10px" }}
          noWrap
        >
          {caption}
        </Typography>
        <MuiLink
          href={url}
          target="_blank"
          rel="noreferrer"
          variant="caption"
          sx={{
            color: "text.secondary",
            textDecorationColor: "currentColor",
            flexShrink: 0,
            fontSize: "10px",
          }}
        >
          Download
        </MuiLink>
      </Box>
    )}
    <video
      src={url}
      controls
      preload="metadata"
      style={{
        display: "block",
        width: "100%",
        aspectRatio: "16 / 9",
        maxHeight: 200,
        backgroundColor: "black",
      }}
    >
      <track kind="captions" />
    </video>
  </Box>
);

// ============================================================================
// TestRunCard
// ============================================================================

interface TestRunCardProps {
  run: TestRun;
  defaultExpanded: boolean;
}

const TestRunCard = ({ run, defaultExpanded }: TestRunCardProps) => {
  const statusColor = TEST_RUN_STATUS_COLOR[run.run_status] ?? "default";
  const railColor = STATUS_RAIL[statusColor];
  // After `TestRun.ensure()` on the backend, `recording_path` is a
  // presigned URL (see db/models.py ensure_urls). Treat anything starting
  // with http as a playable source.
  const recording = run.recording_path?.startsWith("http") ? run.recording_path : undefined;
  const testName = run.test?.name ?? run.name;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderLeft: "3px solid",
        borderLeftColor: railColor,
        borderRadius: `${RADIUS_MD}px`,
        overflow: "hidden",
        "&:before": { display: "none" },
        boxShadow: "none",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon fontSize="small" />}
        sx={{
          paddingX: 1,
          paddingY: 0,
          minHeight: 36,
          "& .MuiAccordionSummary-content": { margin: "8px 0" },
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ width: "100%", minWidth: 0 }}
        >
          <RunIcon fontSize="small" sx={{ color: BRAND_PRIMARY, flexShrink: 0 }} />
          <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0}>
            <Stack direction="row" spacing={0.75} alignItems="baseline">
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, flexShrink: 0, fontSize: "12px" }}
              >
                Test run
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontFamily: "monospace",
                  flexShrink: 0,
                  fontSize: "10px",
                }}
              >
                {run.id.slice(0, 8)}
              </Typography>
            </Stack>
            {testName && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "10px",
                  lineHeight: 1.3,
                  "@container (max-width: 260px)": { display: "none" },
                }}
                noWrap
              >
                {testName}
              </Typography>
            )}
          </Stack>
          <Box
            sx={{
              flexShrink: 0,
              "@container (max-width: 280px)": {
                "& .MuiChip-label": { display: "none" },
                "& .MuiChip-root": { paddingLeft: "4px", paddingRight: "4px" },
                "& .MuiChip-icon": { marginLeft: 0, marginRight: 0 },
              },
            }}
          >
            <RunStatusChip
              status={run.run_status}
              size="small"
              variant={statusColor === "success" ? "filled" : "outlined"}
            />
          </Box>
          <Tooltip title="Open test run details" arrow>
            <IconButton
              component={Link}
              to={`/test-runs/${run.id}`}
              size="small"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open test run details"
              sx={{
                flexShrink: 0,
                padding: 0.25,
                "@container (max-width: 240px)": { display: "none" },
              }}
            >
              <OpenIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 1, paddingTop: 0 }}>
        {recording ? (
          <RecordingFrame
            url={recording}
            caption={`${testName || "Test run"}${
              run.end_time ? ` · ${new Date(run.end_time).toLocaleTimeString()}` : ""
            }`}
          />
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {run.run_status === "running" || run.run_status === "pending"
              ? "Run in progress — recording will appear when it finishes."
              : "No recording captured for this run."}
          </Typography>
        )}
        {run.error_message && (
          <Typography variant="caption" color="error" sx={{ display: "block", marginTop: 1 }}>
            {run.error_message}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
